import type {
  InfiniteData,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import {
  makeApiProvider,
  useApiQuery,
  usePhoenixApiInfiniteQuery,
  usePrefetchApiQuery,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type { PaginatedResultSet } from '@pflegenavi/shared/utils';
import type {
  GetTransactionsWithReceiptDto,
  TransactionSourceType,
} from '@pflegenavi/shared/api';
import type {
  GetFamilyMemberTransactionsPhoenixParams,
  GetTransactionsPhoenixParams,
  GetTransactionsPhoenixResult,
  ITransactionPhoenixApi,
} from './api';
import { TransactionPhoenixApi } from './api';
import {
  FAMILY_MEMBER_TRANSACTIONS_PHOENIX_KEY,
  TRANSACTIONS_PHOENIX_KEY,
} from './queryKeys';
import {
  convertFamilyMemberTransactionsPhoenixToNodeJS,
  convertPhoenixResultToNodeJS,
} from './converters';

const ApiContext = createContext<ITransactionPhoenixApi | undefined>(undefined);
const {
  useApi: useTransactionPhoenixApi,
  ApiProvider: TransactionPhoenixApiProvider,
} = makeApiProvider({
  name: 'TransactionPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new TransactionPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useTransactionPhoenixApi,
  TransactionPhoenixApiProvider,
  ApiContext as TransactionPhoenixApiContext,
  ITransactionPhoenixApi,
};

const TRANSACTION_STALE_TIME = 1000 * 60 * 2; // 2 minutes

export const useTransactionsPhoenix = (
  params: GetTransactionsPhoenixParams,
  options?: Omit<
    UseQueryOptions<GetTransactionsPhoenixResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetTransactionsPhoenixResult, unknown> => {
  const result = useApiQuery(
    useTransactionPhoenixApi,
    TRANSACTIONS_PHOENIX_KEY(params),
    (api) => {
      return api.getTransactions({ params });
    },
    {
      staleTime: TRANSACTION_STALE_TIME,
      // select: selectTransactions, // Date conversion handled in converter
      ...options,
    }
  );

  return result;
};

export const useTransactionsPhoenixSuspense = (
  params: GetTransactionsPhoenixParams,
  options?: Omit<
    UseSuspenseQueryOptions<GetTransactionsPhoenixResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<GetTransactionsPhoenixResult, unknown> => {
  const result = useSuspenseApiQuery(
    useTransactionPhoenixApi,
    TRANSACTIONS_PHOENIX_KEY(params),
    (api) => {
      return api.getTransactions({ params });
    },
    {
      staleTime: TRANSACTION_STALE_TIME,
      // select: selectTransactions, // Date conversion handled in converter
      ...options,
    }
  );

  return result;
};

export const usePrefetchTransactionsPhoenix = (
  params: GetTransactionsPhoenixParams
): void => {
  const getTransactions = useCallback(
    (api: ITransactionPhoenixApi) => {
      return api.getTransactions({ params });
    },
    [params]
  );

  return usePrefetchApiQuery(
    useTransactionPhoenixApi,
    TRANSACTIONS_PHOENIX_KEY(params),
    getTransactions,
    true
  );
};

export const useGetTransactionsPhoenixCallback: () => (
  params: GetTransactionsPhoenixParams
) => Promise<GetTransactionsPhoenixResult> = () => {
  const api = useTransactionPhoenixApi();
  return useCallback(
    async (params: GetTransactionsPhoenixParams) => {
      const result = await api.getTransactions({ params });
      return result; // Date conversion handled in converter
    },
    [api]
  );
};

export type { GetTransactionsPhoenixParams, GetTransactionsPhoenixResult };

// Infinite query hook for Phoenix transactions with usePhoenixApiInfiniteQuery
// Returns data in legacy format for backward compatibility
export const useTransactionsPhoenixInfiniteQuery = (
  params: Omit<GetTransactionsPhoenixParams, 'page'>,
  options?: {
    enabled?: boolean;
  }
): ReturnType<
  typeof usePhoenixApiInfiniteQuery<
    ITransactionPhoenixApi | undefined,
    InfiniteData<PaginatedResultSet<GetTransactionsWithReceiptDto>>,
    GetTransactionsPhoenixResult
  >
> => {
  const select = useCallback(
    (data: InfiniteData<GetTransactionsPhoenixResult>) => {
      return {
        ...data,
        pages: data.pages.map((page) => convertPhoenixResultToNodeJS(page)),
      };
    },
    []
  );

  return usePhoenixApiInfiniteQuery(
    useTransactionPhoenixApi,
    TRANSACTIONS_PHOENIX_KEY(params),
    async (api: ITransactionPhoenixApi, page: number) => {
      return await api.getTransactions({
        params: {
          ...params,
          page: page, // page is already 1-based from usePhoenixApiInfiniteQuery
        },
      });
    },
    {
      staleTime: TRANSACTION_STALE_TIME,
      select,
      ...options,
    }
  );
};

/**
 * Family-member listing of transactions for a single resident via Phoenix.
 *
 * Mirrors the legacy `useTransactionsForFamilyMember` (NestJS) hook's return
 * shape so existing FM web call sites can swap implementations without
 * touching consumers.
 */
export const useTransactionsForFamilyMemberPhoenix = (
  residentId: string | undefined,
  tenant: string | undefined,
  sourceTypes: readonly TransactionSourceType[] = [],
  familyMemberId?: string | null,
  options?: Omit<
    UseQueryOptions<GetTransactionsWithReceiptDto[], unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetTransactionsWithReceiptDto[], unknown> => {
  const params: GetFamilyMemberTransactionsPhoenixParams = {
    tenant: tenant ?? '',
    resident_id: residentId ?? '',
    family_member_id: familyMemberId === undefined ? undefined : familyMemberId,
    source_types: sourceTypes.length > 0 ? [...sourceTypes] : undefined,
  };

  return useApiQuery(
    useTransactionPhoenixApi,
    FAMILY_MEMBER_TRANSACTIONS_PHOENIX_KEY(params),
    async (api) => {
      const result = await api.getTransactionsForFamilyMember({ params });
      return convertFamilyMemberTransactionsPhoenixToNodeJS(result);
    },
    {
      staleTime: TRANSACTION_STALE_TIME,
      ...options,
      enabled:
        Boolean(residentId) &&
        Boolean(tenant) &&
        (options?.enabled === undefined ? true : options.enabled),
    }
  );
};

// Export converters
export {
  convertNodeJSParamsToPhoenix,
  convertPhoenixResultToNodeJS,
  convertFamilyMemberTransactionsPhoenixToNodeJS,
} from './converters';
